// One supplier (M6): what you buy from it first (supplier items: product, their code, pack
// and size, lead time, current price) with "New request" as the page's action; its price list
// versions and which is in effect (UI6A-8); then its business contact details (shown only
// here, never elsewhere) as a summary with an Edit button (UI6A-14); where it came from and
// which fields a person edited (D-058/D-060), with any import changes waiting for a person.
// The items table fits 1280 (UI6A-104): the lead time folds into the pack, provenance is a
// priority column that folds under the product below 1360 px (as production's tables), and
// row actions are icon buttons with their names as tooltips.

import type { SupplierDetail, SupplierItem, SupplierUpdateRequest } from '@enp/domain/api';
import {
  Badge,
  DEFAULT_TIME_ZONE,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  FormField,
  IconButton,
  Input,
  Money,
  PageHeader,
  Skeleton,
  StatusBadge,
  Switch,
  Textarea,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  ChevronRight,
  FileText,
  Pencil,
  Plus,
  Star,
  TriangleAlert,
  UserPen,
} from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { NARROW_ONLY, WIDE_COLUMN } from '../../../../../production/parts.js';
import { priceListState } from '../../../../../purchasing/figures.js';
import {
  KeyHints,
  NoPermission,
  PriceListStateBadge,
  ProvenanceBadges,
  PurchasingBreadcrumb,
  Section,
  useCan,
  usePageKeys,
} from '../../../../../purchasing/parts.js';
import {
  invalidatePurchasing,
  openReviewItemsQuery,
  priceListsQuery,
  supplierQuery,
  supplierReviewItems,
  updateSupplier,
  updateSupplierItem,
} from '../../../../../purchasing/queries.js';
import {
  type Draft,
  draftOf,
  ItemDialog,
  SupplierReviews,
  supplierChanges,
} from '../../../../../purchasing/supplier-parts.js';
import {
  daysText,
  discountText,
  leadTimeText,
  packsText,
  packText,
  vatSwitchHelp,
} from '../../../../../purchasing/words.js';
import { AccountDate, useAccountToday } from '../../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../../shell/page.js';
import { ErrorPanel } from '../../../../../shell/route-states.js';
import { Notice } from '../../../../../stations/page-parts.js';
import { calendarToday, plainDateLabel } from '../../../../../stock/labels.js';
import { actionProblem } from '../../../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/purchasing/suppliers/$supplierId/')({
  component: SupplierPage,
});

// ---------------------------------------------------------------- details

const TEXT_FIELDS = [
  { key: 'contact_name', label: 'Contact person', max: 200 },
  { key: 'email', label: 'Email', max: 200 },
  { key: 'phone', label: 'Phone', max: 60 },
  { key: 'website', label: 'Website', max: 300 },
] as const;

function Owned({ supplier, field }: { supplier: SupplierDetail; field: string }) {
  if (!supplier.person_owned_fields.includes(field)) return null;
  return (
    <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-primary-text">
      <UserPen aria-hidden className="size-3.5" />
      <span className="sr-only">, </span>
      edited here
    </span>
  );
}

/** The details as text: what a person reads when not editing (and all a viewer sees). */
function DetailsSummary({ supplier }: { supplier: SupplierDetail }) {
  const rows: [string, string, ReactNode][] = [
    ['name', 'Name', supplier.name],
    ['code', 'Code', supplier.code ?? 'None'],
    [
      'lead_time_days',
      'Lead time',
      supplier.lead_time_days === null ? 'Not set' : daysText(supplier.lead_time_days),
    ],
    [
      'vat_registered',
      'VAT',
      supplier.vat_registered ? 'VAT-registered' : 'Not VAT-registered: no VAT on its invoices',
    ],
    ['contact_name', 'Contact person', supplier.contact_name ?? 'None'],
    ['email', 'Email', supplier.email ?? 'None'],
    ['phone', 'Phone', supplier.phone ?? 'None'],
    ['website', 'Website', supplier.website ?? 'None'],
    ['address', 'Address', supplier.address ?? 'None'],
    ['note', 'Note', supplier.note ?? 'None'],
  ];
  return (
    <dl
      aria-label="Supplier details"
      data-testid="supplier-details"
      className="grid gap-x-6 gap-y-3 text-body sm:grid-cols-2 xl:grid-cols-3"
    >
      {rows.map(([field, label, value]) => (
        <div key={field} className="flex min-w-0 flex-col">
          <dt className="text-sm text-muted">
            {label}
            <Owned supplier={supplier} field={field} />
          </dt>
          <dd className="break-words text-fg">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function DetailsForm({
  supplier,
  canEdit,
  onDone,
}: {
  supplier: SupplierDetail;
  canEdit: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [d, setD] = useState<Draft>(() => draftOf(supplier));
  const [errors, setErrors] = useState<{ name?: string; lead?: string }>({});
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }));
  const changes = supplierChanges(supplier, d);
  const save = useMutation({
    mutationFn: (body: SupplierUpdateRequest) => updateSupplier(supplier.id, body),
    onSuccess: async (s) => {
      await invalidatePurchasing(queryClient);
      setD(draftOf(s));
      toast.success(`${s.name}: details saved`, {
        description: 'Fields you changed are yours now: an import won’t overwrite them.',
      });
      onDone();
    },
  });
  const failed = save.isError ? actionProblem(save.error, 'The details weren’t saved') : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!d.name.trim()) next.name = 'A supplier needs a name.';
    if (d.lead.trim() !== '') {
      const n = Number(d.lead);
      if (!Number.isInteger(n) || n < 0 || n > 365) next.lead = 'Whole days, 0 to 365, or empty.';
    }
    setErrors(next);
    if (next.name || next.lead || !changes) return;
    save.mutate(changes);
  };
  const disabled = !canEdit || supplier.archived_at !== null;
  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4"
      noValidate
      aria-label="Supplier details"
      data-testid="supplier-details"
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <FormField
          label={
            <>
              Name
              <Owned supplier={supplier} field="name" />
            </>
          }
          required
          error={errors.name}
        >
          <Input
            value={d.name}
            disabled={disabled}
            maxLength={200}
            onChange={(e) => set('name', e.target.value)}
          />
        </FormField>
        <FormField
          label={
            <>
              Code
              <Owned supplier={supplier} field="code" />
            </>
          }
        >
          <Input
            value={d.code}
            disabled={disabled}
            maxLength={40}
            onChange={(e) => set('code', e.target.value)}
          />
        </FormField>
        <FormField
          label={
            <>
              Lead time (days)
              <Owned supplier={supplier} field="lead_time_days" />
            </>
          }
          error={errors.lead}
          description="From ordering to delivery; an item can have its own."
        >
          <Input
            inputMode="numeric"
            value={d.lead}
            disabled={disabled}
            onChange={(e) => set('lead', e.target.value)}
          />
        </FormField>
        {TEXT_FIELDS.map((f) => (
          <FormField
            key={f.key}
            label={
              <>
                {f.label}
                <Owned supplier={supplier} field={f.key} />
              </>
            }
          >
            <Input
              value={d[f.key]}
              disabled={disabled}
              maxLength={f.max}
              type={f.key === 'email' ? 'email' : 'text'}
              onChange={(e) => set(f.key, e.target.value)}
            />
          </FormField>
        ))}
        <FormField
          label={
            <>
              Address
              <Owned supplier={supplier} field="address" />
            </>
          }
          className="sm:col-span-2"
        >
          <Textarea
            rows={2}
            value={d.address}
            disabled={disabled}
            maxLength={500}
            onChange={(e) => set('address', e.target.value)}
          />
        </FormField>
        <FormField
          label={
            <>
              Note
              <Owned supplier={supplier} field="note" />
            </>
          }
          className="sm:col-span-2 xl:col-span-3"
        >
          <Textarea
            rows={2}
            value={d.note}
            disabled={disabled}
            maxLength={2000}
            onChange={(e) => set('note', e.target.value)}
          />
        </FormField>
        <FormField
          label={
            <>
              VAT-registered
              <Owned supplier={supplier} field="vat_registered" />
            </>
          }
          inline
          description={vatSwitchHelp(d.vat_registered)}
          className="sm:col-span-2 xl:col-span-3"
        >
          {(control) => (
            <Switch
              {...control}
              disabled={disabled}
              checked={d.vat_registered}
              onCheckedChange={(v) => set('vat_registered', v)}
            />
          )}
        </FormField>
      </div>
      {failed && (
        <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
          {failed.explain}
        </Notice>
      )}
      {!disabled && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-sm text-muted" aria-live="polite">
            {changes
              ? `${Object.keys(changes).length} unsaved ${Object.keys(changes).length === 1 ? 'change' : 'changes'}`
              : 'No changes'}
          </span>
          <Button type="button" variant="secondary" onClick={onDone}>
            Cancel
          </Button>
          <Button type="submit" disabled={!changes} loading={save.isPending}>
            Save details
          </Button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------- items

function ItemsTable({
  supplier,
  canEdit,
  onEdit,
}: {
  supplier: SupplierDetail;
  canEdit: boolean;
  onEdit: (item: SupplierItem) => void;
}) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: (i: SupplierItem) => updateSupplierItem(i.id, { archived: i.archived_at === null }),
    onSuccess: async (i) => {
      await invalidatePurchasing(queryClient);
      toast.success(i.archived_at ? `${i.product.name} archived` : `${i.product.name} restored`, {
        description: i.archived_at
          ? 'It is no longer offered for requests or the restock suggestion.'
          : undefined,
      });
    },
    onError: (e) => {
      const p = actionProblem(e, 'It wasn’t changed');
      toast.error(p.title, { description: p.explain });
    },
  });
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table aria-label="What you buy from them" className="w-full border-collapse text-body">
        <thead>
          <tr className="bg-surface-sunken text-left text-xs font-semibold uppercase tracking-wide text-muted">
            <th scope="col" className="px-3 py-2">
              Product
            </th>
            <th scope="col" className="px-3 py-2">
              Pack · lead time
            </th>
            <th scope="col" className="whitespace-nowrap px-3 py-2 text-right">
              Price now
            </th>
            <th scope="col" className={cn('whitespace-nowrap px-3 py-2', WIDE_COLUMN)}>
              Where from
            </th>
            <th scope="col" className="hidden whitespace-nowrap px-3 py-2 lg:table-cell">
              Last ordered
            </th>
            {canEdit && (
              <th scope="col" className="px-3 py-2">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {supplier.items.map((i) => (
            <tr
              key={i.id}
              className="border-t border-border align-top"
              data-archived={i.archived_at ? 'true' : undefined}
            >
              <td className="px-3 py-2">
                <span className="flex flex-col gap-0.5 leading-tight">
                  <span className="flex flex-wrap items-center gap-2 font-medium text-fg">
                    {i.product.name}
                    {i.preferred && (
                      <Badge tone="accent">
                        <Star aria-hidden />
                        Preferred
                      </Badge>
                    )}
                    {i.archived_at && <Badge tone="neutral">Archived</Badge>}
                  </span>
                  <span className="text-xs text-muted">
                    {i.supplier_code ? (
                      <>
                        Their code <span className="whitespace-nowrap">{i.supplier_code}</span>
                      </>
                    ) : (
                      'No supplier code'
                    )}
                    {i.effective_vat_rate_code === 'zero_rated' ? ' · zero-rated' : ''}
                  </span>
                  {/* Below 1360 px the Where-from column is gone: provenance folds here. */}
                  <span className={cn('mt-1', NARROW_ONLY)}>
                    <ProvenanceBadges source={i.source} personOwned={i.person_owned_fields} />
                  </span>
                </span>
              </td>
              <td className="px-3 py-2 text-sm text-fg">
                <span className="flex flex-col leading-tight">
                  <span className="whitespace-nowrap">{packText(i, i.product.base_uom)}</span>
                  <span className="whitespace-nowrap text-xs text-muted" data-testid="item-lead">
                    {leadTimeText(i, supplier.lead_time_days)}
                  </span>
                  {i.min_order_packs && (
                    <span className="whitespace-nowrap text-xs text-muted">
                      At least {packsText(i.min_order_packs, i.pack_uom)}
                    </span>
                  )}
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                {i.current_price ? (
                  <span className="flex flex-col items-end leading-tight">
                    <Money
                      cents={i.current_price.net_price_cents}
                      className="font-medium text-fg"
                    />
                    <span className="whitespace-nowrap text-xs text-muted">
                      {i.current_price.discount_bp > 0 ? (
                        <>
                          List <Money cents={i.current_price.list_price_cents} /> less{' '}
                          {discountText(i.current_price.discount_bp)} ·{' '}
                        </>
                      ) : null}
                      v{i.current_price.version_no}
                    </span>
                  </span>
                ) : (
                  <span className="flex items-center justify-end gap-1 whitespace-nowrap text-sm font-medium text-state-warning">
                    <TriangleAlert aria-hidden className="size-4" />
                    No price
                  </span>
                )}
              </td>
              <td className={cn('px-3 py-2', WIDE_COLUMN)}>
                <ProvenanceBadges source={i.source} personOwned={i.person_owned_fields} />
              </td>
              <td className="hidden px-3 py-2 text-sm lg:table-cell">
                {i.last_ordered ? (
                  <span className="flex flex-col leading-tight">
                    <span className="whitespace-nowrap text-fg">
                      {packsText(i.last_ordered.qty_packs, i.pack_uom)},{' '}
                      <AccountDate value={i.last_ordered.ordered_at} />
                    </span>
                    <Link
                      to="/purchasing/orders/$orderId"
                      params={{ orderId: i.last_ordered.po_id }}
                      className="w-fit whitespace-nowrap rounded-sm font-mono text-xs text-primary-text underline-offset-2 hover:underline"
                    >
                      {i.last_ordered.po_number}
                    </Link>
                  </span>
                ) : (
                  <span className="text-muted">Never</span>
                )}
              </td>
              {canEdit && (
                <td className="px-3 py-2">
                  <span className="flex justify-end gap-1">
                    {i.archived_at === null && (
                      <IconButton
                        label={`Edit ${i.product.name}`}
                        icon={<Pencil aria-hidden />}
                        onClick={() => onEdit(i)}
                      />
                    )}
                    <IconButton
                      label={`${i.archived_at ? 'Restore' : 'Archive'} ${i.product.name}`}
                      icon={
                        i.archived_at ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />
                      }
                      loading={toggle.isPending && toggle.variables?.id === i.id}
                      onClick={() => toggle.mutate(i)}
                    />
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- price lists

function PriceLists({ supplier, canEdit }: { supplier: SupplierDetail; canEdit: boolean }) {
  const navigate = useNavigate();
  const q = useQuery(priceListsQuery(supplier.id));
  const today = useAccountToday() ?? calendarToday(DEFAULT_TIME_ZONE);
  const live = supplier.items.filter((i) => i.archived_at === null);
  return (
    <Section
      id="supplier-price-lists"
      title="Price lists"
      actions={
        canEdit && supplier.archived_at === null && live.length > 0 ? (
          <Button asChild variant="secondary">
            <Link
              to="/purchasing/suppliers/$supplierId/price-lists/new"
              params={{ supplierId: supplier.id }}
            >
              <Plus aria-hidden />
              New price list
            </Link>
          </Button>
        ) : undefined
      }
    >
      <p className="text-sm text-muted">
        Each version takes effect on its date; the newest one in effect gives today’s prices. List
        price and discount are kept apart, so a change in discount is flagged.
      </p>
      {q.isError ? (
        <ErrorPanel
          error={q.error}
          titleAs="h2"
          what="The price lists"
          onRetry={() => void q.refetch()}
        />
      ) : q.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : q.data.items.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-strong/70 px-4 py-6 text-center text-body text-muted">
          No prices yet.{' '}
          {live.length === 0
            ? 'Add what you buy from them first, then a price list.'
            : canEdit
              ? 'Add a price list so requests and orders carry prices.'
              : 'Someone who orders stock can add one.'}
        </p>
      ) : (
        <ol
          aria-label="Price list versions"
          className="flex flex-col divide-y divide-border rounded-md border border-border"
        >
          {q.data.items.map((v) => (
            <li key={v.id} data-version={v.version_no}>
              <button
                type="button"
                onClick={() =>
                  void navigate({
                    to: '/purchasing/price-lists/$priceListId',
                    params: { priceListId: v.id },
                  })
                }
                className="flex w-full flex-wrap items-center justify-between gap-3 px-3 py-2 text-left hover:bg-surface-hover"
              >
                <span className="flex flex-col leading-tight">
                  <span className="font-medium text-fg">
                    Version {v.version_no} · from{' '}
                    <time dateTime={v.effective_from}>
                      {plainDateLabel(v.effective_from, today)}
                    </time>
                  </span>
                  <span className="text-sm text-muted">
                    {v.item_count} {v.item_count === 1 ? 'price' : 'prices'} ·{' '}
                    {v.origin === 'import'
                      ? 'imported'
                      : `entered by ${v.created_by?.name ?? 'someone'}`}
                    , <AccountDate value={v.created_at} />
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  {v.discount_changes > 0 && (
                    <Badge tone="neutral">
                      {v.discount_changes} discount{' '}
                      {v.discount_changes === 1 ? 'change' : 'changes'}
                    </Badge>
                  )}
                  <PriceListStateBadge
                    state={priceListState(v, q.data.items, today)}
                    effectiveFrom={v.effective_from}
                    archivedAt={v.archived_at}
                    today={today}
                  />
                  <ChevronRight aria-hidden className="size-4 text-muted" />
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- archive

function ArchiveDialog({ supplier, onClose }: { supplier: SupplierDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const archived = supplier.archived_at !== null;
  const run = useMutation({
    mutationFn: () => updateSupplier(supplier.id, { archived: !archived }),
    onSuccess: async (s) => {
      await invalidatePurchasing(queryClient);
      toast.success(s.archived_at ? `${s.name} archived` : `${s.name} restored`);
      onClose();
    },
  });
  const failed = run.isError ? actionProblem(run.error, 'It wasn’t changed') : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent role="alertdialog" className="max-w-md">
        <DialogForm
          onSubmit={(e) => {
            e.preventDefault();
            run.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {archived ? `Restore ${supplier.name}?` : `Archive ${supplier.name}?`}
            </DialogTitle>
            <DialogDescription>
              {archived
                ? 'They can be asked for stock again.'
                : 'Nothing is deleted: open orders stay, and the history stays. They can’t be asked for stock until restored.'}
            </DialogDescription>
          </DialogHeader>
          {failed && (
            <DialogBody>
              <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
                {failed.explain}
              </Notice>
            </DialogBody>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              autoFocus
              variant={archived ? 'primary' : 'destructiveSolid'}
              loading={run.isPending}
            >
              {archived ? 'Restore' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- the page

const editableOrOpen = (s: SupplierDetail) => s.archived_at === null;

function SupplierPage() {
  const { supplierId } = Route.useParams();
  const q = useQuery(supplierQuery(supplierId));
  const reviews = useQuery(openReviewItemsQuery());
  const canEdit = useCan('purchasing.order', true);
  const canRequest = useCan('purchasing.request');
  const [itemDialog, setItemDialog] = useState<{ item: SupplierItem | null } | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [editing, setEditing] = useState(false);
  const s = q.data;
  const navigate = useNavigate();
  useDocumentTitle(s ? s.name : 'Supplier');
  // n new request, i add an item, e edit details (UI6A-110).
  const liveNow = s ? s.items.some((i) => i.archived_at === null) : false;
  const open = s !== undefined && s.archived_at === null;
  usePageKeys({
    n:
      open && canRequest && liveNow
        ? () => void navigate({ to: '/purchasing/requests/new', search: { supplier: s.id } })
        : undefined,
    i: open && canEdit ? () => setItemDialog({ item: null }) : undefined,
    e: open && canEdit && !editing ? () => setEditing(true) : undefined,
  });

  if (q.isError && !s) {
    return (
      <>
        <PurchasingBreadcrumb
          trail={[{ to: '/purchasing/suppliers', label: 'Suppliers' }]}
          page="Supplier"
        />
        <div className="mt-4 max-w-2xl">
          <ErrorPanel
            error={q.error}
            titleAs="h1"
            what="This supplier"
            onRetry={() => void q.refetch()}
            extraAction={
              <Button variant="secondary" asChild>
                <Link to="/purchasing/suppliers">Back to suppliers</Link>
              </Button>
            }
          />
        </div>
      </>
    );
  }
  if (!s) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <h1 className="sr-only">Supplier</h1>
        <span role="status" className="sr-only">
          Loading the supplier…
        </span>
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  const mine = supplierReviewItems(reviews.data?.items, s.id);
  const editable = canEdit === true && s.archived_at === null;
  const live = s.items.filter((i) => i.archived_at === null).length;

  return (
    <>
      <PageHeader
        eyebrow={
          <PurchasingBreadcrumb
            trail={[{ to: '/purchasing/suppliers', label: 'Suppliers' }]}
            page={s.name}
          />
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            {s.name}
            {s.archived_at && (
              <StatusBadge status="neutral" className="font-body">
                Archived
              </StatusBadge>
            )}
            {!s.vat_registered && (
              <Badge tone="neutral" className="font-body">
                Not VAT-registered
              </Badge>
            )}
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            <ProvenanceBadges source={s.source} personOwned={s.person_owned_fields} />
            <span>
              {s.imported_at ? (
                <>
                  Last imported <AccountDate value={s.imported_at} />
                </>
              ) : s.source ? (
                'Imported'
              ) : null}
              {s.imported_at && s.lead_time_days !== null && ' · '}
              {s.lead_time_days !== null && `Lead time ${daysText(s.lead_time_days)}`}
            </span>
          </span>
        }
        actions={
          canRequest && s.archived_at === null && live > 0 ? (
            <Button asChild>
              <Link to="/purchasing/requests/new" search={{ supplier: s.id }} aria-keyshortcuts="n">
                <Plus aria-hidden />
                New request
              </Link>
            </Button>
          ) : undefined
        }
      />
      <div className="flex flex-col gap-5">
        <span className="-mb-2 flex justify-end">
          <KeyHints
            keys={[
              ...(canRequest && editableOrOpen(s) && live > 0
                ? ([['n', 'new request']] as const)
                : []),
              ...(editable ? ([['i', 'add an item']] as const) : []),
              ...(editable && !editing ? ([['e', 'edit details']] as const) : []),
            ]}
          />
        </span>
        <SupplierReviews items={mine} />
        {canEdit === false && (
          <NoPermission>
            You can look but not change: suppliers, their items and prices are edited by people who
            order stock for every branch.
          </NoPermission>
        )}
        <Section
          id="supplier-items"
          title={`What you buy from them (${live})`}
          actions={
            editable ? (
              <Button
                variant="secondary"
                icon={<Plus aria-hidden />}
                aria-keyshortcuts="i"
                onClick={() => setItemDialog({ item: null })}
              >
                Add an item
              </Button>
            ) : undefined
          }
        >
          {s.items.length === 0 ? (
            <p className="rounded-md border border-dashed border-border-strong/70 px-4 py-6 text-center text-body text-muted">
              Nothing yet. An item is a product you buy from {s.name}, in their pack (a 12.5 kg bag,
              a case of 24).
            </p>
          ) : (
            <ItemsTable
              supplier={s}
              canEdit={editable}
              onEdit={(item) => setItemDialog({ item })}
            />
          )}
        </Section>

        <PriceLists supplier={s} canEdit={canEdit === true} />

        <Section
          id="supplier-details"
          title="Details"
          actions={
            canEdit ? (
              <>
                {!editing && s.archived_at === null && (
                  <Button
                    variant="secondary"
                    icon={<Pencil aria-hidden />}
                    aria-keyshortcuts="e"
                    onClick={() => setEditing(true)}
                  >
                    Edit details…
                  </Button>
                )}
                <Button
                  variant={s.archived_at ? 'secondary' : 'destructive'}
                  icon={s.archived_at ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />}
                  onClick={() => setArchiving(true)}
                >
                  {s.archived_at ? 'Restore…' : 'Archive supplier…'}
                </Button>
              </>
            ) : undefined
          }
        >
          <p className="text-sm text-muted">
            Business contact details. They show only on this page: never on orders, stations or
            reports.
          </p>
          {editing && canEdit ? (
            <DetailsForm key={s.updated_at} supplier={s} canEdit onDone={() => setEditing(false)} />
          ) : (
            <DetailsSummary supplier={s} />
          )}
        </Section>
        <p className="flex items-center gap-2 text-sm text-muted">
          <FileText aria-hidden className="size-4" />
          Added <AccountDate value={s.created_at} /> · last changed{' '}
          <AccountDate value={s.updated_at} withTime />
        </p>
      </div>
      {itemDialog && (
        <ItemDialog supplier={s} item={itemDialog.item} onClose={() => setItemDialog(null)} />
      )}
      {archiving && <ArchiveDialog supplier={s} onClose={() => setArchiving(false)} />}
    </>
  );
}
